import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Avatar,
  Modal,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Sidebar from "../sideBar";
import { allPendingRequests } from "../../../state/slices/admin";
import { adminApproveRequest, adminRejectRequest, clearState } from "../../../state/slices/request";
import { useSelector, useDispatch } from "react-redux";
import { GridOverlay } from "@mui/x-data-grid";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { alpha } from "@mui/material";
import Loading from "../../../components/loading/loading";
import RequestDetailPage from "../RequestDetailPage";
import { useNavigate } from "react-router-dom";


const PendingRequestPage = () => {
  const nav = useNavigate();
  const theme = useMuiTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const dispatch = useDispatch();
  const { pendingRequests, loading } = useSelector((state) => state.admin);
  const {success,loading:loadingRequest} = useSelector((state) => state.request);
  if(success){
    dispatch(clearState());
    dispatch(allPendingRequests());
  }
  useEffect(() => {
    dispatch(allPendingRequests());
  }, [dispatch]);

  const columns = [
    { field: "studentName", headerName: t('dashboard.studentName'), width: 150 },
    { field: "tutorName", headerName: t('dashboard.tutorName'), width: 150 },
    { field: "studentPhone", headerName: t('dashboard.studentPhone'), width: 150 },
    { field: "tutorPhone", headerName: t('dashboard.tutorPhone'), width: 150 },
    { field: "studentEmail", headerName: t('dashboard.studentEmail'), width: 200 },
    { field: "tutorEmail", headerName: t('dashboard.tutorEmail'), width: 200 },
    { field: "startingTime", headerName: t('dashboard.startingTime'), width: 150 },
    { field: "message", headerName: t('dashboard.message'), width: 200 },
    { field: "tutorRate", headerName: t('dashboard.tutorRate'), width: 150 },
    { field: "tutoringType",
     headerName: t('dashboard.tutoringType'),
      width: 200,
      renderCell: (params) => (
        params.row.tutoringType.map((type) => (
          <Chip
            label={type == "IN_PERSON" ? t('common.inPerson') : t('common.online')}
            variant="outlined"
            color="primary"
            size="small"
            sx={{ m: 0.5 }}
          />
      )))
    },
    { field: "adminStatus", headerName: t('dashboard.adminStatus'), width: 120 },
    { field: "tutorStatus", headerName: t('dashboard.tutorStatus'), width: 120 },
    {
      field: "actions",
      headerName: t('dashboard.actions'),
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  console.log("Approve");
                  dispatch(adminApproveRequest(params.row.id));
                  
                }}
                disabled={!selectedRow || selectedRow.id !== params.row.id}
              >
                {t('dashboard.approve')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  console.log("Reject");
                  dispatch(adminRejectRequest(params.row.id));
                }}
                disabled={!selectedRow || selectedRow.id !== params.row.id}
              >
                {t('dashboard.reject')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      ),
    },
  ];

  const rows = pendingRequests.map((request) => ({
    id: request._id,
    studentName: `${request.studentId.userId.firstName} ${request.studentId.userId.lastName}`,
    tutorName: `${request.tutorId.userId.firstName} ${request.tutorId.userId.lastName}`,
    studentPhone: request.studentId.userId.phoneNumber,
    tutorPhone: request.tutorId.userId.phoneNumber,
    studentEmail: request.studentId.userId.email,
    tutorEmail: request.tutorId.userId.email,
    startingTime: request.startingTime,
    message: request.message,
    tutorRate: request.tutorId.rate,
    tutoringType: request.tutoringType,
    adminStatus: request.adminApproved,
    tutorStatus: request.status,
  }));


  const CustomNoRowsOverlay = () => (
    <GridOverlay>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          color: "secondary",
        }}
      >
        <Typography variant="h6">{t('dashboard.noPendingRequests')}</Typography>
      </Box>
    </GridOverlay>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
      <Grid container>
        <Grid item xs={12} md={2}>
          <Sidebar />
        </Grid>
        <Grid item xs={12} md={10}>
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: theme.palette.primary.main, 
                mb: 3 
              }}
            >
              {t('dashboard.requestDetails')} - {t('request.pending')}
            </Typography>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <div style={{ height: 600, width: "100%" }}>
                {loading || loadingRequest ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Loading />
                  </Box>
                ) : (
                  <DataGrid
                    columns={columns}
                    rows={rows}
                    columnHeaderHeight={60}
                    onCellClick={(params) => {
                      const disabledColumns = ["actions"];
                      if (disabledColumns.includes(params.field)) {
                        return;
                      }
                      setSelectedRow(params.row);
                      handleOpen();
                    }}
                    components={{
                      NoRowsOverlay: CustomNoRowsOverlay,
                    }}
                    sx={{
                      '& .MuiDataGrid-cell:hover': {
                        cursor: 'pointer',
                      },
                      '& .MuiDataGrid-row:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      }
                    }}
                  />
                )}
              </div>
            </Paper>
          </Box>
        </Grid>
        {selectedRow && <RequestDetailPage open={open} handleClose={handleClose} data={pendingRequests && pendingRequests.find((request) => request._id === selectedRow.id)} />}
      </Grid>
    </Box>
  );
};

export default PendingRequestPage;
